import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  Typography,
  Chip,
  CircularProgress,
  InputAdornment,
  IconButton,
  Paper,
  ClickAwayListener,
  Popper,
} from '@mui/material';
import {
  Clear,
  KeyboardArrowDown,
} from '@mui/icons-material';

const SearchableDropdown = ({
  // Required props
  label,
  value,
  onChange,
  options = [],
  
  // Optional props
  placeholder = "Search...",
  loading = false,
  error = false,
  helperText = "",
  disabled = false,
  required = false,
  fullWidth = true,
  size = "medium",
  
  // Display options
  displayKey = "name",
  valueKey = "id",
  descriptionKey = "description",
  statusKey = "status",
  
  // Customization
  showDescription = false,
  showStatus = false,
  showCount = false,
  countKey = "product_count",
  
  // Actions
  onClear = null,
  allowClear = true,
  clearValue = null, // Value to return when clearing (default: '' for strings, [] for arrays)
  
  // Styling
  variant = "outlined",
  sx = {},
  
  // Search functionality
  searchable = true,
  minSearchLength = 1,
  
  // Multiple selection
  multiple = false,
  renderValue = null,
  
  // Custom rendering
  renderOption = null,
  renderChip = null,
  
  // Callbacks
  onSearch = null,
  onOpen = null,
  onClose = null,
  
  // Additional props
  ...otherProps
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [inputValue, setInputValue] = useState('');
  const dropdownRef = useRef(null);
  const anchorRef = useRef(null);

  // Get selected option for display
  const getSelectedOption = useCallback(() => {
    if (multiple) {
      if (Array.isArray(value) && value.length > 0) {
        return options.find(opt => opt[valueKey] === value[0]);
      }
      return null;
    }
    return options.find(opt => opt[valueKey] === value);
  }, [value, options, valueKey, multiple]);

  // Set initial input value based on selected option
  useEffect(() => {
    const selected = getSelectedOption();
    setInputValue(selected ? selected[displayKey] : '');
  }, [value, options, displayKey, valueKey, multiple]);

  // Filter options based on search term
  useEffect(() => {
    if (!searchable || searchTerm.length < minSearchLength) {
      setFilteredOptions(options);
      return;
    }

    const filtered = options.filter(option => {
      const displayValue = option[displayKey] || '';
      const description = option[descriptionKey] || '';
      const searchLower = searchTerm.toLowerCase();
      
      return (
        displayValue.toLowerCase().includes(searchLower) ||
        description.toLowerCase().includes(searchLower)
      );
    });
    
    setFilteredOptions(filtered);
    
    // Call external search function if provided
    if (onSearch) {
      onSearch(searchTerm, filtered);
    }
  }, [searchTerm, options, searchable, minSearchLength, displayKey, descriptionKey, onSearch]);

  // Reset search when dropdown closes
  useEffect(() => {
    if (!open) {
      setSearchTerm('');
    }
  }, [open]);



  const handleInputClick = () => {
    if (!disabled) {
      setOpen(true);
      onOpen?.();
    }
  };

  const handleInputChange = (event) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    setSearchTerm(newValue);
    
    // If input is cleared, clear the selection
    if (!newValue) {
      onChange?.(multiple ? [] : '');
      onClear?.();
    }
  };

  const handleOptionClick = (option) => {
    const optionValue = option[valueKey];
    
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(optionValue)
        ? currentValues.filter(v => v !== optionValue)
        : [...currentValues, optionValue];
      onChange?.(newValues);
    } else {
      onChange?.(optionValue);
      setInputValue(option[displayKey]);
      setOpen(false);
      onClose?.();
    }
  };

  const handleClearValue = () => {
    const valueToSet = clearValue !== null ? clearValue : (multiple ? [] : '');
    onChange?.(valueToSet);
    setInputValue('');
    onClear?.();
  };

  const handleClickAway = () => {
    setOpen(false);
    onClose?.();
  };

  // Default render option function
  const defaultRenderOption = (option) => (
    <Box sx={{ width: '100%', p: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {option[displayKey]}
        </Typography>
        {showCount && option[countKey] !== undefined && (
          <Chip 
            label={option[countKey]} 
            size="small" 
            variant="outlined"
            sx={{ ml: 1 }}
          />
        )}
      </Box>
      {showDescription && option[descriptionKey] && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {option[descriptionKey]}
        </Typography>
      )}
      {showStatus && option[statusKey] && (
        <Chip 
          label={option[statusKey]} 
          size="small" 
          color={option[statusKey] === 'active' ? 'success' : 'default'}
          sx={{ mt: 0.5 }}
        />
      )}
    </Box>
  );

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ width: fullWidth ? '100%' : 'auto', ...sx }}>
        {/* Label */}
        {label && (
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ mb: 0.5, fontWeight: 500 }}
          >
            {label}
            {required && <span style={{ color: 'error.main' }}> *</span>}
          </Typography>
        )}

        {/* Main Input Field */}
        <Box ref={anchorRef}>
          <TextField
            ref={dropdownRef}
            fullWidth
            size={size}
            variant={variant}
            value={inputValue}
            onChange={handleInputChange}
            onClick={handleInputClick}
            placeholder={placeholder}
            disabled={disabled}
            error={error}
            // Prevent browser autofill suggestions (addresses, phone numbers, etc.)
            autoComplete="off"
            inputProps={{
              ...(otherProps.inputProps || {}),
              autoComplete: 'off',
              ...(searchable ? {} : { readOnly: true }),
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {inputValue && allowClear && (
                    <IconButton
                      size="small"
                      onClick={handleClearValue}
                      sx={{ color: 'error.main' }}
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  )}
                  <KeyboardArrowDown 
                    fontSize="small" 
                    sx={{ 
                      transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s'
                    }}
                  />
                </InputAdornment>
              ),
            }}
            {...otherProps}
          />
        </Box>

        {/* Helper Text */}
        {helperText && (
          <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ mt: 0.5 }}>
            {helperText}
          </Typography>
        )}

        {/* Dropdown Popper */}
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
        >
          <Paper 
            elevation={8}
            sx={{ 
              maxHeight: 300, 
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              mt: 0.5
            }}
          >


            {/* Loading State */}
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={20} />
              </Box>
            )}

            {/* No Results */}
            {!loading && filteredOptions.length === 0 && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {searchTerm ? 'No results found' : 'No options available'}
                </Typography>
              </Box>
            )}

            {/* Options */}
            {!loading && filteredOptions.map((option) => (
              <Box
                key={option[valueKey]}
                onClick={() => handleOptionClick(option)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': {
                    borderBottom: 'none',
                  },
                }}
              >
                {renderOption ? renderOption(option) : defaultRenderOption(option)}
              </Box>
            ))}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default SearchableDropdown; 